import React from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { Loader } from '@orijinworks/frontend-commons';
import { withStyles } from '@material-ui/styles';
import PermissionCatalogSections from '../../../../v2/modules/create-staff/components/permission-sections';
import { buildPermissionSections } from '../../../../v2/modules/create-staff/utils/staffPermissionSections';
import {
  permissionStepErrorText,
  permissionsProfileTabHeader,
  permissionsProfileTabTitle,
  permissionsProfileTabIntro,
  permissionsProfileEditButton,
  permissionsProfileTabBody,
  permissionsProfileGroupOverline,
  cancelButton,
  getCreateButtonStyles,
} from '../../../../v2/modules/create-staff/CreateStaff.styles';
import useStaffPermissionsTabHook from './useStaffPermissionsTabHook';
import staffProfileStyles from './staff-profile.style';

/**
 * Staff profile Permissions tab: effective role defaults ± overrides from Janus.
 * Renders one section per catalog group (header from {@code groupDisplayName}); LFA-gated
 * sections (e.g. Reentry Planning) only appear when their gate passes for the subject's
 * facility(ies). View: ON/OFF chips. Edit: toggles + Cancel / Save.
 */
function StaffPermissionsTab({
  personId,
  hasActiveLocation = true,
  canEdit,
  onSaveSuccess,
  locationIds = [],
  classes,
}) {
  const {
    items,
    toggleState,
    loading,
    saving,
    error,
    isDirty,
    showEditChrome,
    handleStartEdit,
    handleCancelEdit,
    handleSave,
    handleTogglePermission,
  } = useStaffPermissionsTabHook({ personId, canEdit, onSaveSuccess, hasActiveLocation, locationIds });

  if (!hasActiveLocation) {
    return (
      <Box width="100%">
        <Box className={classes.facilitiesSectionHeader}>
          <Box>
            <Typography component="h2" className={classes.facilitiesHeading}>
              Permissions Unavailable
            </Typography>
            <Typography className={classes.facilitiesExplainer}>
              This user account is currently inactive. You must reactivate the user to view or modify their
              permissions.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <Loader isLoading />
      </Box>
    );
  }

  if (error && items.length === 0) {
    return (
      <Typography style={permissionStepErrorText} component="p">
        {error}
      </Typography>
    );
  }

  const sections = buildPermissionSections(items, { locationIds });

  return (
    <Box width="100%">
      <Box style={permissionsProfileTabHeader}>
        <Box flex={1} minWidth={0}>
          <Typography component="h2" style={permissionsProfileTabTitle}>
            Permissions
          </Typography>
          <Typography component="p" style={permissionsProfileTabIntro}>
            The user will have these permissions across all facilities where these features are enabled.
          </Typography>
        </Box>
        {canEdit && !showEditChrome ? (
          <Button
            variant="outlined"
            color="primary"
            style={permissionsProfileEditButton}
            startIcon={<EditIcon style={{ fontSize: 16, color: 'var(--primary-blue-primary-main, #0F48B8)' }} />}
            onClick={handleStartEdit}
          >
            Edit Permissions
          </Button>
        ) : null}
      </Box>

      <Box style={permissionsProfileTabBody}>
        {error ? (
          <Typography style={permissionStepErrorText} component="p">
            {error}
          </Typography>
        ) : null}

        <PermissionCatalogSections
          sections={sections}
          toggleState={toggleState}
          editable={showEditChrome}
          onToggle={handleTogglePermission}
          sectionLabelSx={permissionsProfileGroupOverline}
          headingComponent="h3"
        />

        {showEditChrome ? (
          <Box display="flex" justifyContent="flex-end" alignItems="center" flexWrap="wrap">
            <Box mr={1.5} display="inline-block">
              <Button variant="outlined" style={cancelButton} onClick={handleCancelEdit} disabled={saving}>
                Cancel
              </Button>
            </Box>
            <Button
              variant="contained"
              style={getCreateButtonStyles(!isDirty || saving)}
              onClick={handleSave}
              disabled={!isDirty || saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export default withStyles(staffProfileStyles)(StaffPermissionsTab);
